import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import useMediaQuery from '@mui/material/useMediaQuery';
import { createAppTheme } from '../theme';

type ColorMode = 'light' | 'dark';

interface ColorModeContextType {
  mode: ColorMode;
  toggleMode: () => void;
}

const STORAGE_KEY = 'joan-color-mode';

const ColorModeContext = createContext<ColorModeContextType | undefined>(undefined);

const readSavedMode = (): ColorMode | null => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'light' || saved === 'dark' ? saved : null;
  } catch {
    return null;
  }
};

// Follows the device setting until the user picks a mode, then remembers it
export const ColorModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });
  const [savedMode, setSavedMode] = useState<ColorMode | null>(readSavedMode);
  const mode: ColorMode = savedMode ?? (prefersDark ? 'dark' : 'light');

  useEffect(() => {
    // The CSS colour tokens in index.css switch on this attribute
    document.documentElement.setAttribute('data-theme', mode);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', mode === 'dark' ? '#0E1816' : '#FFFFFF');
  }, [mode]);

  const value = useMemo(() => ({
    mode,
    toggleMode: () => {
      const next: ColorMode = mode === 'dark' ? 'light' : 'dark';
      setSavedMode(next);
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Private mode or blocked storage: the choice lasts for this visit only
      }
    }
  }), [mode]);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export const useColorMode = () => {
  const context = useContext(ColorModeContext);
  if (context === undefined) {
    throw new Error('useColorMode must be used within a ColorModeProvider');
  }
  return context;
};
